import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import {
  DEFAULT_GA4_MEASUREMENT_ID,
  createGa4Pageview,
  isPublicTrackablePath,
  normalizeGa4MeasurementId,
  SEO_PAGEVIEW_READY_EVENT,
} from "@/lib/googleAnalytics";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function getSetting(settings: Array<{ key: string; value: string | null }> | undefined, key: string) {
  return settings?.find((setting) => setting.key === key)?.value;
}

/**
 * Carrega GA4 somente quando um Measurement ID válido foi salvo no Admin.
 * O pageview inicial e as mudanças de rota do Wouter são enviados manualmente.
 */
export function GoogleAnalytics() {
  const [location] = useLocation();
  const { data: settings } = trpc.siteConfig.storeSettings.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
  });
  const measurementId = useMemo(
    () => normalizeGa4MeasurementId(getSetting(settings, "ga4_measurement_id")) ?? DEFAULT_GA4_MEASUREMENT_ID,
    [settings]
  );
  const configuredId = useRef<string | null>(null);
  const initialPageviewHandledByHead = useRef<string | null>(null);
  const lastTrackedLocation = useRef<string | null>(null);

  useEffect(() => {
    if (!measurementId || configuredId.current === measurementId) return;

    const scriptId = `google-analytics-${measurementId}`;
    const serverScript = document.querySelector(
      `script[src="https://www.googletagmanager.com/gtag/js?id=${measurementId}"]`
    );
    if (!serverScript && !document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
      document.head.appendChild(script);
    }

    window.dataLayer = window.dataLayer ?? [];
    window.gtag = window.gtag ?? ((...args: unknown[]) => window.dataLayer?.push(args));
    if (serverScript) {
      // O gtag no HTML inicial já registrou o primeiro pageview desta carga.
      initialPageviewHandledByHead.current = measurementId;
    } else {
      window.gtag("js", new Date());
      window.gtag("config", measurementId, { send_page_view: false });
    }
    configuredId.current = measurementId;
  }, [measurementId]);

  useEffect(() => {
    if (!measurementId || configuredId.current !== measurementId) return;

    // O carregamento inicial já é medido pelo gtag do HTML. Memorizar a rota evita
    // que o primeiro evento SEO de uma navegação seja confundido com o carregamento.
    if (initialPageviewHandledByHead.current === measurementId && lastTrackedLocation.current === null) {
      lastTrackedLocation.current = location;
    }
  }, [location, measurementId]);

  useEffect(() => {
    const handleSeoPageview = (rawEvent: Event) => {
      if (!measurementId || configuredId.current !== measurementId || !window.gtag) return;

      const detail = (rawEvent as CustomEvent<{ path?: unknown; title?: unknown; url?: unknown }>).detail;
      if (
        !detail ||
        typeof detail.path !== "string" ||
        typeof detail.title !== "string" ||
        typeof detail.url !== "string" ||
        !isPublicTrackablePath(detail.path) ||
        lastTrackedLocation.current === detail.path
      ) {
        return;
      }

      window.gtag("event", "page_view", createGa4Pageview(detail.path, detail.title, detail.url));
      lastTrackedLocation.current = detail.path;
    };

    window.addEventListener(SEO_PAGEVIEW_READY_EVENT, handleSeoPageview);
    return () => window.removeEventListener(SEO_PAGEVIEW_READY_EVENT, handleSeoPageview);
  }, [measurementId]);

  return null;
}
